import { useState, useEffect } from 'react';

const AdvancedSearchModal = ({ 
  isOpen, 
  closeModal, 
  filterOptions, 
  selectedFilters, 
  dateRange, 
  dueToday, 
  todaysAdmission, 
  pendingFeesList,
  pendingFeesOverdue, // NEW prop for overdue fees
  onApply,
  onClear,
  onLocationChange // Add this prop
}) => {
  const [tempFilters, setTempFilters] = useState({
    location: '',
    branch: '',
    courseType: '',
  });
  
  const [tempDateRange, setTempDateRange] = useState({
    fromDate: '',
    toDate: ''
  });
  
  // Changed to single string value instead of multiple booleans
  const [tempQuickFilter, setTempQuickFilter] = useState('');

  /* --------------------------------------------------------- *
   * 1️⃣ Fetch the full location‑branch mapping once on mount   *
   * --------------------------------------------------------- */
  const [allLocationBranches, setAllLocationBranches] = useState([]); // every row from /api/location
  const [filteredBranches, setFilteredBranches] = useState([]);       // drives the Branch dropdown

  useEffect(() => {
    const fetchLocationBranches = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/location`);
        const data = await res.json();
        setAllLocationBranches(data);
      } catch (err) {
        console.error("Failed to fetch location mapping:", err);
      }
    };
    fetchLocationBranches();
  }, []);

  /* --------------------------------------------------------- *
   * 2️⃣ Recompute dropdown whenever location selection changes *
   * --------------------------------------------------------- */
  useEffect(() => {
    if (!tempFilters.location) {
      setFilteredBranches([]);
      setTempFilters(prev => ({ ...prev, branch: '' }));
      return;
    }

    const branchesForSelectedLocation = allLocationBranches
      .filter(
        row =>
          row.location === tempFilters.location &&                    // location matches selection
          (filterOptions.allBranches || []).includes(row.branch)      // …and user is authorised
      )
      .map(row => row.branch);

    const unique = Array.from(new Set(branchesForSelectedLocation));
    setFilteredBranches(unique);

    // auto‑deselect branch if it's no longer valid for the selected location
    if (tempFilters.branch && !unique.includes(tempFilters.branch)) {
      setTempFilters(prev => ({ ...prev, branch: '' }));
    }
  }, [tempFilters.location, allLocationBranches, filterOptions.allBranches]);

  // Initialize temp states when modal opens
  useEffect(() => {
    if (isOpen) {
      setTempFilters(selectedFilters);
      setTempDateRange(dateRange);
      
      // Determine which quick filter is currently active
      let activeQuickFilter = '';
      if (dueToday) activeQuickFilter = 'dueToday';
      else if (todaysAdmission) activeQuickFilter = 'todaysAdmission';
      else if (pendingFeesList) activeQuickFilter = 'pendingFeesList';
      else if (pendingFeesOverdue) activeQuickFilter = 'pendingFeesOverdue'; // NEW
      
      setTempQuickFilter(activeQuickFilter);
    }
  }, [isOpen, selectedFilters, dateRange, dueToday, todaysAdmission, pendingFeesList, pendingFeesOverdue]);

  // Handle filter changes
  const handleFilterChange = (type, value) => {
    if (type === 'location' && value !== tempFilters.location) {
      // Reset branch selection if location changes
      setTempFilters(prev => ({
        ...prev,
        [type]: value,
        branch: '' // Reset branch when location changes
      }));
      
      // Call the onLocationChange callback to update available branches
      if (onLocationChange) {
        onLocationChange(value);
      }
    } else {
      setTempFilters(prev => ({
        ...prev,
        [type]: value
      }));
    }
  };

  const handleDateRangeChange = (type, value) => {
    setTempDateRange(prev => ({
      ...prev,
      [type]: value
    }));
  };

  // Simplified quick filter handler
  const handleQuickFilterChange = (value) => {
    setTempQuickFilter(value);
  };

  const handleApply = () => {
    // Convert single quick filter back to boolean format for compatibility
    const quickFilters = {
      dueToday: tempQuickFilter === 'dueToday',
      todaysAdmission: tempQuickFilter === 'todaysAdmission',
      pendingFeesList: tempQuickFilter === 'pendingFeesList',
      pendingFeesOverdue: tempQuickFilter === 'pendingFeesOverdue' // NEW
    };

    onApply({
      filters: tempFilters,
      dateRange: tempDateRange,
      quickFilters: quickFilters
    });
    closeModal();
  };

  const handleClear = () => {
    const clearedFilters = {
      location: '',
      branch: '',
      courseType: '',
    };
    const clearedDateRange = {
      fromDate: '',
      toDate: ''
    };

    setTempFilters(clearedFilters);
    setTempDateRange(clearedDateRange);
    setTempQuickFilter(''); // Clear quick filter

    onClear();
    closeModal();
  };

  if (!isOpen) return null;

  return (
    <div className="advanced-search-modal-overlay">
      <div className="advanced-search-modal">
        {/* Modal Header */}
        <div className="advanced-search-header">
          <h2>Advanced Search</h2>
          <button 
            className="modal-close-btn" 
            onClick={closeModal}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Modal Content */}
        <div className="advanced-search-content">
          {/* Location and Branch Filters */}
          <div className="filter-section">
            <h3>Location & Branch</h3>
            <div className="filter-row">
              <div className="filter-group">
                <label>Location:</label>
                <select
                  className="advanced-select-dropdown"
                  value={tempFilters.location}
                  onChange={e => handleFilterChange('location', e.target.value)}
                >
                  <option value="">All Locations</option>
                  {(filterOptions.locations || []).map((loc, idx) => (
                    <option key={idx} value={loc}>{loc}</option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label>Branch:</label>
                <select
                  className="advanced-select-dropdown"
                  value={tempFilters.branch}
                  onChange={e => handleFilterChange('branch', e.target.value)}
                >
                  <option value="">All Branches</option>
                  {filteredBranches.map((br, idx) => (
                    <option key={idx} value={br}>{br}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Course Type Filter */}
          <div className="filter-section">
            <h3>Course Type</h3>
            <div className="filter-row">
              <div className="filter-group">
                <label>Course Type:</label>
                <select
                  className="advanced-select-dropdown"
                  value={tempFilters.courseType}
                  onChange={e => handleFilterChange('courseType', e.target.value)}
                >
                  <option value="">All Course Types</option>
                  {(filterOptions.courseTypes || []).map((ct, idx) => (
                    <option key={idx} value={ct}>{ct}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Date Range Filter */}
          <div className="filter-section">
            <h3>Date Range for Admission</h3>
            <div className="filter-row">
              <div className="filter-group">
                <label>From Date:</label>
                <input
                  type="date"
                  className="advanced-date-input"
                  value={tempDateRange.fromDate}
                  onChange={(e) => handleDateRangeChange('fromDate', e.target.value)}
                />
              </div>
              <div className="filter-group">
                <label>To Date:</label>
                <input
                  type="date"
                  className="advanced-date-input"
                  value={tempDateRange.toDate}
                  onChange={(e) => handleDateRangeChange('toDate', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Quick Filters - Now as Dropdown with Overdue Option */}
          <div className="filter-section">
            <h3>Quick Filters</h3>
            <div className="filter-row">
              <div className="filter-group">
                <label>Filter Type:</label>
                <select
                  className="advanced-select-dropdown"
                  value={tempQuickFilter}
                  onChange={e => handleQuickFilterChange(e.target.value)}
                >
                  <option value="">No Quick Filter</option>
                  <option value="dueToday">Payments Due Today</option>
                  <option value="todaysAdmission">Today's Admission</option>
                  <option value="pendingFeesList">Pending Fees List</option>
                  <option value="pendingFeesOverdue">Pending Fees Overdue</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Actions */}
        <div className="advanced-search-actions">
          <button 
            className="btn-clear-advanced" 
            onClick={handleClear}
          >
            Clear All
          </button>
          <button 
            className="btn-apply-advanced" 
            onClick={handleApply}
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdvancedSearchModal;